import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  getContactInfoByStoreId,
  getStoreInfoByStoreId,
  getUserNameByUserId,
  MerchantOrder,
  MerchantOrderItemOption,
} from '../../components/EditMerchantOrder/HandleStatusChange';
import { ResponsiveTable } from '../../components/Layout';
import MerchantOrderPdf, {
  registerFont,
} from '../../components/MerchantOrderPdf/MerchantOrderPdf';
import { Communicator } from '../../tools/Communication';
import { download } from '@/utils/download';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Document, pdf } from '@react-pdf/renderer';
import { ContentSection } from '../../components/Layout/ContentSection';
import { Routes } from '@/utils/routes';

type MerchantOrderOverviewTabProps = {
  vytalStoreId: string | null;
  title: string;
};

export const MerchantOrderOverviewTab = ({
  vytalStoreId,
  title,
}: MerchantOrderOverviewTabProps) => {
  const buttonLinkStyle = {
    background: 'none',
    border: 'none',
    padding: '0!important',
    color: '#538637',
    textDecoration: 'underline',
    cursor: 'pointer',
  };

  const [merchantOrders, setMerchantOrders] = useState([] as MerchantOrder[]);
  const [merchantOrderItemOptions, setMerchantOrderItemOptions] = useState(
    [] as MerchantOrderItemOption[],
  );
  const [cities, setCities] = useState([] as string[]);
  useEffect(() => {
    Communicator.Get(`/MerchantOrder/GetMerchantOrders`, {
      storeId: vytalStoreId,
    })
      .then((res) => res.json())
      .then((orders: MerchantOrder[]) => {
        const ordersUpdated = orders.map((o) => {
          return {
            ...o,
            revision: {
              ...o.revision,
              orderExecutionDate: new Date(
                o.revision.orderExecutionDate as any,
              ),
              items: o.revision.items.map((i: any) => ({
                ...i,
                amount: i.amountInt,
              })),
            },
          };
        });
        setMerchantOrders(ordersUpdated);
      });
    Communicator.Get('/Config/GetConfigValue', { key: 'MerchantOrderConfig' })
      .then((response) => response.json())
      .then((config) => {
        setCities(config.Cities);
      });
    Communicator.Get('/api/3/MerchantOrder/GetOrderItemOptions', {
      includeAllContainers: true,
    })
      .then((response) => response.json())
      .then((options: MerchantOrderItemOption[]) => {
        setMerchantOrderItemOptions(options);
      });
  }, []);

  async function downloadPdf(order: MerchantOrder) {
    // Event-based orders don't support PDF generation (no targetStoreId)
    if (!order.targetStoreId) {
      alert('PDF generation is only available for store-based orders');
      return;
    }

    registerFont();
    const [storeInfoByStoreId, userNameByUserId, contactInfoByStoreId] =
      await Promise.all([
        getStoreInfoByStoreId([order]),
        getUserNameByUserId([order]),
        getContactInfoByStoreId([order]),
      ]);
    const pages = await MerchantOrderPdf(
      order,
      cities,
      storeInfoByStoreId[order.targetStoreId].appUserId,
      userNameByUserId[order.createdByUserId],
      contactInfoByStoreId[order.targetStoreId],
      merchantOrderItemOptions,
    );

    let filename = `MerchantOrder_${order.deliveryNoteId}.pdf`;
    const doc = <Document>{pages}</Document>;
    const blob = await pdf(doc).toBlob();
    download(blob, filename);
  }

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <ResponsiveTable className="mt-4" fixed={true}>
        <thead>
          <tr>
            <th>{i18n.t('Common:id')}</th>
            <th>{i18n.t('Common:status')}</th>
            <th>{i18n.t('MerchantOrder:executionDate')}</th>
            <th>{i18n.t('Event:event')}</th>
            <th>{i18n.t('MerchantOrder:deliveryNote')}</th>
          </tr>
        </thead>
        <tbody>
          {merchantOrders.map((order, index) => {
            const isEventOrder = order.eventId != null;
            return (
              <tr key={order.deliveryNoteId}>
                <td>{order.deliveryNoteId}</td>
                <td>
                  {i18n.t(
                    `MerchantOrder:StatusReadable:${order.revision.status}`,
                  )}
                </td>
                <td>
                  {new Date(
                    order.revision.orderExecutionDate,
                  ).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(false),
                  )}
                </td>
                <td>
                  {isEventOrder ? (
                    <div>
                      <a href={Routes.eventDetail(order.eventId!)}>
                        {order.eventName || order.eventId}
                      </a>
                      {order.eventAccountId && (
                        <>
                          <br />
                          <a
                            href={Routes.eventAccountDetail(
                              order.eventAccountId,
                            )}
                            style={{ fontSize: '0.85em' }}
                          >
                            {order.eventAccountName || order.eventAccountId}
                          </a>
                        </>
                      )}
                    </div>
                  ) : (
                    <span>-</span>
                  )}
                </td>
                <td style={buttonLinkStyle} onClick={() => downloadPdf(order)}>
                  {i18n.t('MerchantOrder:download')}
                </td>
              </tr>
            );
          })}
        </tbody>
      </ResponsiveTable>
    </ContentSection>
  );
};
